import { Controller } from "@hotwired/stimulus";

const CARD_LINK_SELECTOR = 'a[href*="#report_dashboard_card_"]';
const MAX_ATTEMPTS = 20;

// Scroll a dashboard card into view and flash it (like Mo-edited POS cards) when a
// #report_dashboard_card_<id> anchor targets it, on initial load and on click of such a link. We own
// the scroll because the browser's native anchor jump is top-aligned and, on load, fights our scroll.
export default class extends Controller {
  connect() {
    this.timeouts = [];
    this.onClick = (event) => {
      const link = event.target.closest(CARD_LINK_SELECTOR);
      if (!link) return;
      const id = new URL(link.href).hash.slice(1);
      if (!this.element.querySelector(`#${CSS.escape(id)}`)) return;
      event.preventDefault();
      this.#highlight(id, MAX_ATTEMPTS);
    };
    document.addEventListener("click", this.onClick);

    const id = window.location.hash.slice(1);
    if (!id) return;
    // Drop the fragment so the browser doesn't do its own top-aligned jump (and re-jump as the card's
    // chart lazy-loads), then own the scroll ourselves once the card is present and layout has settled.
    history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
    this.#highlight(id, MAX_ATTEMPTS);
  }

  disconnect() {
    document.removeEventListener("click", this.onClick);
    this.timeouts.forEach(clearTimeout);
  }

  #highlight(id, attemptsLeft) {
    const card = this.element.querySelector(`#${CSS.escape(id)}`);
    if (!card) {
      // On a full page load the target card (far down the grid) may not be parsed yet — retry briefly.
      if (attemptsLeft > 0)
        this.timeouts.push(
          setTimeout(() => this.#highlight(id, attemptsLeft - 1), 100),
        );
      return;
    }
    // Cards' charts load lazily and shift layout for a beat after load, so re-assert the scroll a few
    // times to keep the card centered as things settle, rather than scrolling once to a moving target.
    [0, 150, 400, 800].forEach((ms) =>
      this.timeouts.push(setTimeout(() => this.#scrollTo(card), ms)),
    );
    card.classList.remove("reload-flash");
    void card.offsetWidth; // reflow so the animation restarts if a flash is still in flight
    card.classList.add("reload-flash");
    card.addEventListener(
      "animationend",
      () => card.classList.remove("reload-flash"),
      { once: true },
    );
  }

  #scrollTo(card) {
    card.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}
